import { useState, Fragment } from 'react'
import type { PricingTier } from '@/types/pricing'

interface ComparisonRow {
  label: string
  /**
   * If a tier has any of these module slugs (or '*' wildcard), the row is ✓ for that tier.
   */
  requires: string[]
}

interface ComparisonGroup {
  group: string
  rows: ComparisonRow[]
}

const groups: ComparisonGroup[] = [
  {
    group: 'Meetings & recordings',
    rows: [
      { label: 'In-person & virtual recording', requires: ['meetings'] },
      { label: 'AI transcription', requires: ['meetings'] },
      { label: 'AI file notes & action items', requires: ['meetings'] },
      { label: 'Meeting-based reports', requires: ['meetings'] },
    ],
  },
  {
    group: 'Reports & AI',
    rows: [
      { label: 'Full report builder', requires: ['reports_full'] },
      { label: 'AI report generation', requires: ['reports_full'] },
      { label: 'Sparks AI assistant', requires: ['sparks'] },
      { label: 'Knowledge base & RAG', requires: ['knowledge_base'] },
    ],
  },
  {
    group: 'Client management',
    rows: [
      { label: 'Basic client list', requires: ['meetings', 'clients_basic', 'clients_full'] },
      { label: 'Full client management', requires: ['clients_full'] },
      { label: 'Portfolios & investments', requires: ['advisory_tools'] },
      { label: 'Cash flow & tax planning', requires: ['advisory_tools'] },
      { label: 'Sales pipeline', requires: ['pipeline'] },
    ],
  },
  {
    group: 'Client-facing & compliance',
    rows: [
      { label: 'Branded client portal (coming soon)', requires: ['client_portal'] },
      { label: 'Compliance & vulnerability', requires: ['compliance'] },
      { label: 'Workflows & approvals', requires: ['workflows'] },
    ],
  },
]

const includes = (tier: PricingTier, requires: string[]): boolean => {
  if (tier.modules.includes('*')) return true
  return requires.some((slug) => tier.modules.includes(slug))
}

function CheckMark() {
  return (
    <svg className="w-5 h-5 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function Cross() {
  return (
    <svg className="w-5 h-5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

export interface PlanComparisonProps {
  tiers: PricingTier[]
}

export default function PlanComparison({ tiers }: PlanComparisonProps) {
  const [openTier, setOpenTier] = useState<string | null>(tiers[0]?.slug ?? null)
  const visibleTiers = tiers.filter((t) => t.monthlyPrice !== null)
  const enterpriseTier = tiers.find((t) => t.isContactSales)

  return (
    <section className="px-6 py-20 max-w-[1200px] mx-auto" aria-labelledby="plan-comparison-heading">
      <h2 id="plan-comparison-heading" className="font-serif text-3xl sm:text-4xl text-slate-900 text-center mb-3">
        Compare plans
      </h2>
      <p className="text-slate-600 text-center mb-12 max-w-[640px] mx-auto">
        Every tier includes meetings + AI transcription as a foundation. Add reporting, advisory tools, and client-facing features as you need them.
      </p>

      {/* Desktop: full grid */}
      <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th scope="col" className="py-4 px-6 text-sm font-semibold text-slate-500 uppercase tracking-wide">
                Feature
              </th>
              {visibleTiers.map((tier) => (
                <th
                  key={tier.slug}
                  scope="col"
                  className={`py-4 px-6 text-center text-sm font-semibold ${
                    tier.popular ? 'text-accent-teal' : 'text-slate-700'
                  }`}
                >
                  {tier.displayName}
                  {tier.popular && (
                    <span className="block text-[10px] font-bold tracking-wider uppercase text-accent-teal mt-1">
                      Most popular
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.group}>
                <tr className="bg-slate-50/50">
                  <th
                    scope="rowgroup"
                    colSpan={visibleTiers.length + 1}
                    className="py-3 px-6 text-xs font-bold tracking-wider uppercase text-slate-500"
                  >
                    {group.group}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={`${group.group}-${row.label}`} className="border-t border-slate-100">
                    <th scope="row" className="py-3 px-6 text-sm font-medium text-slate-700 text-left">
                      {row.label}
                    </th>
                    {visibleTiers.map((tier) => (
                      <td key={tier.slug} className="py-3 px-6 text-center">
                        {includes(tier, row.requires) ? <CheckMark /> : <Cross />}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        {enterpriseTier && (
          <p className="text-center text-xs text-slate-500 px-6 py-4 bg-slate-50 border-t border-slate-200">
            Enterprise includes everything above plus volume discounts, unlimited usage, SSO/SAML, and dedicated support.
          </p>
        )}
      </div>

      {/* Mobile: per-tier accordion */}
      <div className="lg:hidden space-y-3">
        {visibleTiers.map((tier) => {
          const isOpen = openTier === tier.slug
          return (
            <div
              key={tier.slug}
              className={`bg-white border rounded-xl overflow-hidden ${
                tier.popular ? 'border-accent-teal' : 'border-slate-200'
              }`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between p-5 text-left"
                onClick={() => setOpenTier(isOpen ? null : tier.slug)}
                aria-expanded={isOpen}
                aria-controls={`comparison-${tier.slug}`}
              >
                <span className="font-semibold text-slate-900">{tier.displayName}</span>
                <svg
                  className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {isOpen && (
                <div id={`comparison-${tier.slug}`} className="px-5 pb-5 space-y-5 border-t border-slate-100 pt-4">
                  {groups.map((group) => (
                    <div key={group.group}>
                      <p className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-2">
                        {group.group}
                      </p>
                      <ul className="space-y-2">
                        {group.rows.map((row) => (
                          <li key={row.label} className="flex items-start gap-3 text-sm text-slate-700">
                            <span className="mt-0.5">
                              {includes(tier, row.requires) ? <CheckMark /> : <Cross />}
                            </span>
                            <span className={includes(tier, row.requires) ? '' : 'text-slate-400 line-through'}>
                              {row.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
